import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  PiClockCountdown,
  PiDownloadSimple,
  PiFileArrowDown,
  PiLinkBreak,
  PiXCircle,
} from "react-icons/pi";

import useUiContext from "@/custolink-hooks/useUiContext";
import { FrontendDownloadBundleApi } from "@/custolink-transpiled_by_server/bc.asset_files.api_methods";
import { FrontendDownloadBundleApi as FrontendDownloadBundleApiGeneric } from "@/custolink-transpiled_by_server/bc.asset_files.api_generic";
import type { FrontendDownloadBundleApi$GetInfoForTokenInfo } from "@/custolink-transpiled_by_server/bc.asset_files.api_methods";
import GetPath from "@/custolink-logic/api/GetPath";
import { normaliseHex } from "@/custolink-views/asset_frontend/redesigns/theme";
import { usePreviewFonts } from "@/custolink-views/asset_frontend/redesigns/fonts";
import { AssetShell, PreviewHeader } from "@/custolink-views/asset_frontend/redesigns/tileKit";

/** Same visual weight as formKit's PrimaryButton, as an `<a>` instead of a
 * `<button>`: the download is a plain GET to a URL, not a form submission. */
const DOWNLOAD_LINK_CLASS =
  "rda-tile flex w-full max-w-xs items-center justify-center gap-2 bg-[var(--rda-accent)] px-4 py-3 " +
  "text-sm font-semibold text-[var(--rda-on-accent)]";

/**
 * AssetFilesDownloadLink, asset frontend design: the page a "download this
 * bundle" email link opens on. Behaviour is the classic page's, unchanged -
 * same FrontendDownloadBundleApi.getInfoForToken lookup keyed by the URL
 * token, same download href built by FrontendDownloadBundleApiGeneric via
 * GetPath; only the presentation moves onto tileKit.AssetShell / formKit.
 *
 * Opened from an email on a phone, so this is mobile-first: the org's own
 * header, then one card with the bundle's identity, its expiry and the
 * download action - or, once expired or the token does not resolve, an
 * explanation instead of a dead end.
 */
export default function AssetFilesDownloadLinkDesign() {
  const { token } = useParams();
  const uic = useUiContext();
  usePreviewFonts(["schibsted"]);

  const q = useQuery({
    queryFn: () => FrontendDownloadBundleApi.getInfoForToken(token ?? ""),
    queryKey: ["asset_files_download_link", token],
  });

  const lf = uic.organisationContext.lookAndFeel;
  const accent = normaliseHex(lf.assetFrontendBgcolorHex);

  return (
    <AssetShell accent={accent}>
      <PreviewHeader logoUrl={lf.logoUrl} />

      <div className="flex flex-col gap-3 px-4">
        <div className="rounded-[10px] border border-[color:var(--rda-hairline)] bg-[var(--rda-surface)] px-4 py-5 text-[var(--rda-on-surface)] sm:rounded-2xl sm:py-6">
          {q.isPending ? <PendingState /> : null}
          {!q.isPending && q.data?.info == null ? <InvalidState /> : null}
          {!q.isPending && q.data?.info != null ? (
            <BundleState info={q.data.info} token={token ?? ""} />
          ) : null}
        </div>
      </div>
    </AssetShell>
  );
}

function PendingState() {
  return (
    <div className="flex flex-col items-center gap-3 py-4 text-center">
      <span className="h-8 w-8 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent text-[var(--rda-accent-text)] opacity-70" />
      <p className="text-sm text-[var(--rda-muted)]">Looking up the download link...</p>
    </div>
  );
}

function InvalidState() {
  return (
    <div className="flex flex-col items-center gap-3 py-2 text-center">
      <span
        aria-hidden
        className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--rda-neutral)] text-[var(--rda-muted)]"
      >
        <PiLinkBreak className="text-2xl" />
      </span>
      <div className="flex flex-col gap-1.5">
        <h1 className="text-lg font-semibold tracking-[-0.01em] sm:text-xl">
          This link does not work
        </h1>
        <p className="max-w-[40ch] text-sm text-[var(--rda-muted)]">
          The download link is invalid. Check that you copied the whole link from the email, or
          ask whoever sent it to send a new one.
        </p>
      </div>
    </div>
  );
}

function BundleState(p: { info: FrontendDownloadBundleApi$GetInfoForTokenInfo; token: string }) {
  const { info } = p;

  return (
    <div className="flex flex-col items-center gap-4 text-center">
      <span
        aria-hidden
        className={
          "flex h-12 w-12 items-center justify-center rounded-full text-2xl " +
          (info.expired
            ? "bg-[#FCE8E6] text-[#7A1F17]"
            : "bg-[var(--rda-accent-tint)] text-[var(--rda-accent-text)]")
        }
      >
        {info.expired ? <PiXCircle /> : <PiFileArrowDown />}
      </span>

      <div className="flex flex-col gap-1.5">
        <h1 className="text-lg font-semibold tracking-[-0.01em] sm:text-xl">{info.name}</h1>
        {info.description ? (
          <p className="max-w-[40ch] text-sm text-[var(--rda-on-surface)]">{info.description}</p>
        ) : null}
      </div>

      <ExpiryPill expired={info.expired} expiresAt={info.expiresAt} />

      {info.expired ? (
        <p className="max-w-[40ch] text-sm text-[var(--rda-muted)]">
          This download link has expired and the files are no longer available here. Ask whoever
          sent it for a new link.
        </p>
      ) : (
        <a
          className={DOWNLOAD_LINK_CLASS}
          href={FrontendDownloadBundleApiGeneric.download(GetPath, p.token)}
          rel="noreferrer"
          target="_blank"
        >
          <PiDownloadSimple aria-hidden className="text-lg" />
          Download
        </a>
      )}
    </div>
  );
}

function ExpiryPill(p: { expired: boolean; expiresAt: string }) {
  return (
    <span
      className={
        "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold " +
        (p.expired ? "bg-[#FCE8E6] text-[#7A1F17]" : "bg-[var(--rda-accent-tint)] text-[var(--rda-accent-text)]")
      }
    >
      <PiClockCountdown aria-hidden className="text-sm" />
      {p.expired ? `Expired ${p.expiresAt}` : `Available until ${p.expiresAt}`}
    </span>
  );
}
